/**
 * ExportButton — Dropdown button to export content as Word, PDF, or Excel.
 *
 * Accepts either structured output, raw markdown, or chat messages
 * and triggers a browser download in the chosen format.
 *
 * Content with tabular sheets also offers "Custom export…" (MOR-1048): the
 * report-builder-style tool where the practitioner composes, saves and
 * re-uses their own table over the same rows.
 */

import { useMemo, useState } from "react";
import { saveAs } from "file-saver";
import { Button } from "@/ui/primitives/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/ui/composed/menus/dropdown-menu";
import {
  Download,
  FileText,
  FileSpreadsheet,
  Loader2,
  Table2,
} from "lucide-react";
import { exportToWord, exportToPdf, exportToExcel } from "@/utils/export";
import type { ExportContent } from "@/utils/export";
import {
  CustomExportDialog,
  datasetsFromContent,
} from "@/components/customExport";

type ExportFormat = "word" | "pdf" | "excel";

const ALL_FORMATS: ExportFormat[] = ["word", "pdf", "excel"];

interface ExportButtonProps {
  content: ExportContent;
  /** Base filename without extension */
  filename?: string;
  className?: string;
  variant?: "outline" | "ghost" | "default";
  size?: "sm" | "default" | "lg" | "icon";
  /** Formats shown in the menu. Defaults to Word, PDF, and Excel. */
  formats?: ExportFormat[];
  /** Offer the custom export builder when the content has sheets. */
  customExport?: boolean;
}

const FORMAT_CONFIG: Record<
  ExportFormat,
  { label: string; ext: string; icon: typeof FileText }
> = {
  word: { label: "Word (.docx)", ext: "docx", icon: FileText },
  pdf: { label: "PDF (.pdf)", ext: "pdf", icon: FileText },
  excel: { label: "Excel (.xlsx)", ext: "xlsx", icon: FileSpreadsheet },
};

function sanitizeFilename(name: string): string {
  return name
    .replace(/[^a-zA-Z0-9\s\-_]/g, "")
    .replace(/\s+/g, "_")
    .slice(0, 100);
}

export function ExportButton({
  content,
  filename,
  className,
  variant = "outline",
  size = "sm",
  formats = ALL_FORMATS,
  customExport = true,
}: ExportButtonProps) {
  const [exporting, setExporting] = useState<ExportFormat | null>(null);
  const [customOpen, setCustomOpen] = useState(false);

  const baseName = filename ?? sanitizeFilename(content.title);
  const menuFormats = formats.length > 0 ? formats : ALL_FORMATS;
  const customDatasets = useMemo(
    () => (customExport ? datasetsFromContent(content) : []),
    [customExport, content],
  );

  async function handleExport(format: ExportFormat) {
    setExporting(format);
    try {
      const ext = FORMAT_CONFIG[format].ext;

      const blob: Blob = await (async () => {
        switch (format) {
          case "word":
            return exportToWord(content);
          case "pdf":
            return exportToPdf(content);
          case "excel":
            return exportToExcel(content);
        }
      })();

      saveAs(blob, `${baseName}.${ext}`);
    } catch (err) {
      console.error(`Export to ${format} failed:`, err);
    } finally {
      setExporting(null);
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant}
          size={size}
          className={className}
          disabled={exporting !== null}
        >
          {exporting ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Download className="h-3.5 w-3.5" />
          )}
          <span className="ml-1.5">Export</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        {menuFormats.map((format) => {
          const config = FORMAT_CONFIG[format];
          return (
            <DropdownMenuItem
              key={format}
              onClick={() => handleExport(format)}
              disabled={exporting !== null}
              className="gap-2"
            >
              <config.icon className="h-4 w-4 text-muted-foreground" />
              {config.label}
              {exporting === format && (
                <Loader2 className="ml-auto h-3 w-3 animate-spin" />
              )}
            </DropdownMenuItem>
          );
        })}
        {customDatasets.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => setCustomOpen(true)}
              disabled={exporting !== null}
              className="gap-2"
            >
              <Table2 className="h-4 w-4 text-muted-foreground" />
              Custom export…
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
      {/* Sibling of the menu — Radix unmounts menu content on close. */}
      {customDatasets.length > 0 && (
        <CustomExportDialog
          open={customOpen}
          onOpenChange={setCustomOpen}
          datasets={customDatasets}
          title={content.title}
        />
      )}
    </DropdownMenu>
  );
}
